/**
 * Share stats as images – 4-5 cards for WhatsApp status / social sharing.
 * Sharp modern design with sport confetti. Uses html2canvas to capture.
 */

import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveUid } from '../hooks/useEffectiveUid';
import { useGame } from '../contexts/GameContext';
import { getPlayerProfileData } from '../api/readApi';
import { logUserActivity } from '../services/auditLogService';
import CachedAvatar from './CachedAvatar';
import { GAMES } from '../constants/games';
import './ShareStatsModal.css';

const CARD_W = 1080;
const CARD_H = 1920;

export default function ShareStatsModal({ open, onClose }) {
  const { currentUser, userProfile } = useAuth();
  const effectiveUserId = useEffectiveUid();
  const { gameId } = useGame();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloaded, setDownloaded] = useState(0);
  const cardRefs = useRef({});

  useEffect(() => {
    if (!open || !currentUser?.uid) return;
    setLoading(true);
    setData(null);
    getPlayerProfileData(effectiveUserId ?? currentUser?.uid)
      .then((res) => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    logUserActivity(
      { userId: currentUser.uid, userEmail: currentUser.email, displayName: currentUser.displayName || currentUser.email },
      'share_stats',
      '/share',
      'Share stats modal opened'
    );
  }, [open, effectiveUserId, currentUser?.uid, currentUser?.email, currentUser?.displayName]);

  const displayName = userProfile?.displayName || currentUser?.displayName || currentUser?.email || 'Player';
  const avatarUrl = userProfile?.photoURL || userProfile?.avatarURL || currentUser?.photoURL || '';
  const game = (GAMES || []).find((g) => g.id === gameId) || { id: 'table-tennis', name: 'Table Tennis', icon: '🏓' };

  const getStats = () => {
    if (!data?.statsDoc) return null;
    const gs = (data.statsDoc.gameStats || {})[gameId];
    if (gs) {
      const mw = gs.matchesWon ?? 0;
      const ml = gs.matchesLost ?? 0;
      const total = mw + ml;
      return {
        matchesWon: mw,
        matchesLost: ml,
        totalMatches: total,
        totalPoints: gs.totalPoints ?? 0,
        winRate: total > 0 ? ((mw / total) * 100).toFixed(1) : '0',
      };
    }
    const legacy = (data.statsDoc.matchesWon ?? 0) + (data.statsDoc.matchesLost ?? 0);
    if (legacy > 0) {
      const mw = data.statsDoc.matchesWon ?? 0;
      return {
        matchesWon: mw,
        matchesLost: data.statsDoc.matchesLost ?? 0,
        totalMatches: legacy,
        totalPoints: data.statsDoc.totalPoints ?? 0,
        winRate: ((mw / legacy) * 100).toFixed(1),
      };
    }
    return null;
  };

  const getPlayersPlayedWith = () => {
    const sums = data?.matchSummaries || [];
    const seen = new Set();
    const names = [];
    sums.forEach((m) => {
      (m.opponentNames || []).forEach((n) => {
        if (n && !seen.has(n)) {
          seen.add(n);
          names.push(n);
        }
      });
    });
    return names.slice(0, 12);
  };

  const stats = getStats();
  const playersPlayedWith = getPlayersPlayedWith();

  async function captureCard(key) {
    const el = cardRefs.current[key];
    if (!el) return null;
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      return canvas.toDataURL('image/png');
    } catch (e) {
      console.error('Capture failed:', e);
      return null;
    }
  }

  async function handleDownloadAll() {
    const keys = ['hero', 'matches', 'wins', 'players', 'cta'];
    setGenerating(true);
    setDownloaded(0);
    logUserActivity(
      { userId: currentUser?.uid, userEmail: currentUser?.email, displayName: currentUser?.displayName || currentUser?.email },
      'share_stats',
      '/share',
      'Download all stats images',
      { count: keys.length }
    );
    for (let i = 0; i < keys.length; i++) {
      const dataUrl = await captureCard(keys[i]);
      if (dataUrl) {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `stats-${displayName.replace(/\s+/g, '-')}-${i + 1}.png`;
        a.click();
      }
      setDownloaded(i + 1);
    }
    setGenerating(false);
  }

  async function handleShareOne(key) {
    const dataUrl = await captureCard(key);
    if (!dataUrl) return;
    logUserActivity(
      { userId: currentUser?.uid, userEmail: currentUser?.email, displayName: currentUser?.displayName || currentUser?.email },
      'share_stats',
      '/share',
      'Share single stats image',
      { card: key }
    );
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], 'stats.png', { type: 'image/png' });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'My stats',
          text: `${displayName}'s ${game.name} stats`,
        });
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('Share failed:', e);
      }
    } else {
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `stats-${displayName.replace(/\s+/g, '-')}.png`;
      a.click();
    }
  }

  if (!open) return null;

  return (
    <div className="share-stats-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="share-stats-modal card-ui" onClick={(e) => e.stopPropagation()}>
        <div className="share-stats-modal-header">
          <h2>Share your stats</h2>
          <button type="button" onClick={onClose} className="share-stats-modal-close" aria-label="Close">×</button>
        </div>
        <div className="share-stats-modal-body">
          {loading ? (
            <div className="share-stats-modal-loading">Loading stats…</div>
          ) : !stats ? (
            <p className="share-stats-modal-empty">Play some matches first to share your stats!</p>
          ) : (
            <>
              <p className="share-stats-modal-hint">Preview (optimized for WhatsApp status & social)</p>
              <div className="share-stats-cards-preview">
                {[
                  { key: 'hero', label: '1. Hero' },
                  { key: 'matches', label: '2. Matches' },
                  { key: 'wins', label: '3. Wins' },
                  { key: 'players', label: '4. Players' },
                  { key: 'cta', label: '5. CTA' },
                ].map(({ key, label }) => (
                  <div key={key} className="share-stats-card-wrap">
                    <span className="share-stats-card-label">{label}</span>
                    <div
                      className="share-stats-card-container"
                      style={{ width: CARD_W / 4, height: CARD_H / 4 }}
                    >
                      <div style={{ transform: 'scale(0.25)', transformOrigin: 'top left', width: CARD_W, height: CARD_H }}>
                        <ShareCard
                          type={key}
                          displayName={displayName}
                          avatarUrl={avatarUrl}
                          stats={stats}
                          players={playersPlayedWith}
                          game={game}
                          cardW={CARD_W}
                          cardH={CARD_H}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary btn-small"
                      onClick={() => handleShareOne(key)}
                      disabled={generating}
                    >
                      Share
                    </button>
                  </div>
                ))}
              </div>
              <div className="share-stats-capture-area" aria-hidden>
                {['hero', 'matches', 'wins', 'players', 'cta'].map((key) => (
                  <ShareCard
                    key={key}
                    ref={(r) => { cardRefs.current[key] = r; }}
                    type={key}
                    displayName={displayName}
                    avatarUrl={avatarUrl}
                    stats={stats}
                    players={playersPlayedWith}
                    game={game}
                    cardW={CARD_W}
                    cardH={CARD_H}
                  />
                ))}
              </div>
              <div className="share-stats-modal-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleDownloadAll}
                  disabled={generating}
                >
                  {generating ? `Downloading ${downloaded}/5…` : 'Download all 5 images'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const ShareCard = React.forwardRef(function ShareCard(
  { type, displayName, avatarUrl, stats, players, game, cardW, cardH },
  ref
) {
  const baseStyle = { width: cardW, height: cardH };

  if (type === 'hero') {
    return (
      <div ref={ref} className="share-card share-card-hero" style={baseStyle}>
        <div className="share-card-confetti" aria-hidden />
        <div className="share-card-content">
          <CachedAvatar url={avatarUrl} name={displayName} className="share-card-avatar" alt="" />
          <h1 className="share-card-title">{displayName}</h1>
          <p className="share-card-subtitle">{game.icon} {game.name} Stats</p>
        </div>
      </div>
    );
  }

  if (type === 'matches') {
    return (
      <div ref={ref} className="share-card share-card-matches" style={baseStyle}>
        <div className="share-card-confetti share-card-confetti-gold" aria-hidden />
        <div className="share-card-content">
          <p className="share-card-stat-label">Matches played</p>
          <p className="share-card-stat-value">{stats.totalMatches}</p>
          <p className="share-card-stat-meta">{game.icon} {game.name}</p>
        </div>
      </div>
    );
  }

  if (type === 'wins') {
    return (
      <div ref={ref} className="share-card share-card-wins" style={baseStyle}>
        <div className="share-card-confetti share-card-confetti-green" aria-hidden />
        <div className="share-card-content">
          <p className="share-card-stat-label">Wins</p>
          <p className="share-card-stat-value">{stats.matchesWon}</p>
          <p className="share-card-stat-meta">{stats.winRate}% win rate · {stats.totalPoints} pts</p>
        </div>
      </div>
    );
  }

  if (type === 'players') {
    return (
      <div ref={ref} className="share-card share-card-players" style={baseStyle}>
        <div className="share-card-confetti share-card-confetti-blue" aria-hidden />
        <div className="share-card-content">
          <p className="share-card-stat-label">Players played with</p>
          <div className="share-card-players-list">
            {players.length ? players.slice(0, 8).map((p, i) => (
              <span key={i} className="share-card-player-chip">{p}</span>
            )) : (
              <span className="share-card-player-chip">—</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (type === 'cta') {
    return (
      <div ref={ref} className="share-card share-card-cta" style={baseStyle}>
        <div className="share-card-confetti" aria-hidden />
        <div className="share-card-content">
          <p className="share-card-cta-title">{game.icon} Keep playing!</p>
          <p className="share-card-cta-sub">Track matches · Compete · Have fun</p>
        </div>
      </div>
    );
  }

  return null;
});
